// ============================================================================
// ChatSharedBlock — блок «Файлы/медиа/ссылки» правой панели чата.
// Вложения берутся из ленты (ListMessages: id/name/size/file_type), превью —
// существующий FilePreviewModal (SSoT) через onPreview. Ссылки — SSoT-парсер
// платформы (tokenizeTextLinks/HydraLink): внешние, doc: и object:.
// ============================================================================

import { useMemo, useState } from 'react';
import { FileText, Film, ImageIcon, Link2, Music } from 'lucide-react';
import { TabBar } from '@/components/ui/TabBar';
import { HydraLink } from '@/lib/hydraLinks';
import { mediaKind } from '@/lib/fileName';
import {
  formatBytes,
  formatStamp,
  type ChatAttachment,
  type ChatLink,
} from './chatInfoData';

interface ChatSharedBlockProps {
  attachments: ChatAttachment[];
  links: ChatLink[];
  onPreview: (fileId: string, fileName: string) => void;
}

type SharedTab = 'files' | 'media' | 'links';

function attachmentIcon(att: ChatAttachment) {
  const kind = mediaKind(att.name, att.fileType);
  if (kind === 'image') return <ImageIcon className="h-4 w-4 text-primary" />;
  if (kind === 'video') return <Film className="h-4 w-4 text-primary" />;
  if (kind === 'audio') return <Music className="h-4 w-4 text-primary" />;
  return <FileText className="h-4 w-4 text-muted-foreground" />;
}

export function ChatSharedBlock({ attachments, links, onPreview }: ChatSharedBlockProps) {
  const [tab, setTab] = useState<SharedTab>('files');

  const files = useMemo(
    () => attachments.filter((a) => mediaKind(a.name, a.fileType) === 'file'),
    [attachments],
  );
  const media = useMemo(
    () => attachments.filter((a) => mediaKind(a.name, a.fileType) !== 'file'),
    [attachments],
  );

  const attachmentsList = tab === 'files' ? files : media;

  return (
    <div className="space-y-2">
      <TabBar
        group="chat-info-shared"
        active={tab}
        onChange={(id) => setTab(id as SharedTab)}
        tabs={[
          { id: 'files', label: `Файлы${files.length ? ` (${files.length})` : ''}` },
          { id: 'media', label: `Медиа${media.length ? ` (${media.length})` : ''}` },
          { id: 'links', label: `Ссылки${links.length ? ` (${links.length})` : ''}` },
        ]}
      />

      {tab !== 'links' && (
        attachmentsList.length === 0 ? (
          <div className="glass-panel rounded-xl p-3 text-center text-[11px] text-muted-foreground">
            {tab === 'files' ? 'Файлов в этом чате нет' : 'Медиа в этом чате нет'}
          </div>
        ) : (
          <div className="space-y-1">
            {attachmentsList.map((a) => (
              <button
                key={`${a.messageId}:${a.id}`}
                type="button"
                className="w-full flex items-center gap-2 rounded-lg border border-border/60 bg-card/40 px-2 py-1.5 text-left hover:bg-accent/40"
                onClick={() => onPreview(a.id, a.name)}
                title="Открыть предпросмотр"
              >
                <span className="shrink-0">{attachmentIcon(a)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-medium">{a.name}</span>
                  <span className="block text-[10px] text-muted-foreground">
                    {[formatBytes(a.size), formatStamp(a.createdAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )
      )}

      {tab === 'links' && (
        links.length === 0 ? (
          <div className="glass-panel rounded-xl p-3 text-center text-[11px] text-muted-foreground">
            Ссылок в этом чате нет
          </div>
        ) : (
          <div className="space-y-1">
            {links.map((l) => (
              <div key={l.href} className="rounded-lg border border-border/60 bg-card/40 px-2 py-1.5 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-[11px]" title={l.href}>
                    <HydraLink href={l.href}>{l.label}</HydraLink>
                  </span>
                </div>
                <div className="pl-5 text-[10px] text-muted-foreground">{formatStamp(l.createdAt)}</div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
